/* ──────────────────────────────────────────────────────────────────────────
 * Join as an Investor — IPO Funding Platform intake flow.
 *
 * 1:1 React port of `join_as_investor_flow.html`. Opened from Dashboard-New →
 * "I want to fund IPOs" → "Join as an investor". Three steps: investor type →
 * funding rounds (multi-select) → contact details + indicative amount. On submit
 * it follows the exact same path as every other Contact-Us form: POST
 * /api/contact/lead (lead_type "investor"), so it lands in Admin Center →
 * Contact Leads. All styles scoped under `.dash-new-root .iv*`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import OracleHubBanner from "@/components/OracleHubBanner";
import "./RegisteredInvestor.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INV_TYPES = [
  { key: "hni", icon: "ti-user-dollar", bg: "#E6F1FB", color: "#0C447C", name: "HNI / NII", desc: "High-net-worth / non-institutional individual (above ₹2 L)" },
  { key: "qib", icon: "ti-building-bank", bg: "#E1F5EE", color: "#085041", name: "QIB", desc: "SEBI-registered qualified institutional buyer" },
  { key: "bank", icon: "ti-building-bank", bg: "#FAEEDA", color: "#854F0B", name: "Bank", desc: "Scheduled commercial / co-operative bank" },
  { key: "nbfc", icon: "ti-cash", bg: "#FCEBEB", color: "#791F1F", name: "NBFC", desc: "RBI-registered non-banking financial company" },
  { key: "aif", icon: "ti-trending-up", bg: "#EEEDFE", color: "#3C3489", name: "AIF / VC fund", desc: "Alternative investment / venture capital fund" },
  { key: "family", icon: "ti-home-dollar", bg: "#E6F1FB", color: "#0C447C", name: "Family office", desc: "Private family investment office" },
];
const LABELS = { hni: "HNI / NII", qib: "QIB", bank: "Bank", nbfc: "NBFC", aif: "AIF / VC fund", family: "Family office" };
const NEEDS_REG = { qib: true, bank: true, nbfc: true, aif: true };
const STAGES = [
  { name: "Pre-IPO", desc: "Equity before filing — anchor, placement, at a discount to band" },
  { name: "Bridge rounds", desc: "Short-tenor debt to organise the IPO / cover costs" },
  { name: "Post-IPO", desc: "Stabilisation capital after listing" },
];
const AMOUNTS = ["₹1 – 25 lakh", "₹25 – 50 lakh", "₹50 lakh – 1 crore", "₹1 – 5 crore", "₹5 – 10 crore", "₹10 – 25 crore", "₹25 crore+"];
const HORIZONS = ["Ready now", "Within 3 months", "3 – 6 months", "Opportunistic"];

const JoinInvestorFlow = ({ onBack }) => {
  const [step, setStep] = useState(1); // 1 type | 2 stages | 3 form
  const [done, setDone] = useState(false);
  const [typeKey, setTypeKey] = useState(null);
  const [stages, setStages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", org: "", email: "", mobile: "", amount: "", horizon: "", reg: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const label = typeKey ? LABELS[typeKey] : "—";
  const orgLabel = typeKey === "hni" || typeKey === "family" ? "Entity / family office name" : "Entity / firm name";

  const chooseType = (key) => { setTypeKey(key); setTimeout(() => setStep(2), 150); };
  const toggleStage = (name) => setStages((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  const validate = () => {
    if (!form.name.trim()) return "Contact name is required";
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email.trim())) return "A valid email is required";
    if (!form.mobile.trim() || form.mobile.trim().length < 7) return "A valid mobile number is required";
    if (!form.amount) return "Please select an indicative investment amount";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const lines = [
        `Investor type: ${label}`,
        `Funding rounds: ${stages.length ? stages.join(", ") : "Any round"}`,
        form.org.trim() ? `Entity: ${form.org.trim()}` : null,
        `Approx. amount to invest: ${form.amount}`,
        form.horizon ? `Deployment horizon: ${form.horizon}` : null,
        NEEDS_REG[typeKey] && form.reg.trim() ? `SEBI / RBI registration: ${form.reg.trim()}` : null,
      ].filter(Boolean);

      await axios.post(`${API_URL}/api/contact/lead`, {
        full_name: form.name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        module: `IPO Funding Platform — ${label}`,
        query: lines.join("\n"),
        lead_type: "investor",
      });
      toast.success("Request received! Our capital team will verify and reach out within 2 business days.");
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="iv" data-testid="join-investor-flow">
      <div className="iv-head">
        <div className="iv-kick"><i className="ti ti-coins" aria-hidden="true" /> IPO Funding Platform · Join as an investor</div>
        <div className="iv-h">Get access to vetted MSME deal flow</div>
        <div className="iv-sub">Tell us your investor profile and what you want to fund. Our capital team verifies you and opens curated, KYC-checked deal flow across pre-IPO, bridge and post-IPO rounds.</div>
        <div className="steps">
          <div className={`stp ${step === 1 ? "on" : ""}`}><span className="stp-n">1</span> Investor type</div>
          <div className="stp-line" />
          <div className={`stp ${step === 2 ? "on" : ""}`}><span className="stp-n">2</span> What you fund</div>
          <div className="stp-line" />
          <div className={`stp ${step === 3 ? "on" : ""}`}><span className="stp-n">3</span> Your details</div>
        </div>
      </div>

      {/* STEP 1 */}
      {step === 1 && !done && (
        <div className="iv-card" data-testid="iv-step-type">
          <div className="sec-t">What type of investor are you?</div>
          <div className="sec-d">Categories follow SEBI's investor classification. We verify status before activating deal flow.</div>
          <div className="type-grid">
            {INV_TYPES.map((t) => (
              <button key={t.key} className={`tp ${typeKey === t.key ? "on" : ""}`} onClick={() => chooseType(t.key)} data-testid={`iv-type-${t.key}`}>
                <div className="tp-ic" style={{ background: t.bg, color: t.color }}><i className={`ti ${t.icon}`} aria-hidden="true" /></div>
                <div><div className="tp-n">{t.name}</div><div className="tp-d">{t.desc}</div></div>
              </button>
            ))}
          </div>
          <OracleHubBanner className="iv-oracle" />
          <div className="iv-foot" style={{ marginTop: 14 }}>
            <span className="note"><i className="ti ti-info-circle" style={{ fontSize: 13, color: "#3C3489" }} aria-hidden="true" /> QIBs, banks and NBFCs require registration proof at verification.</span>
            <button className="back" onClick={onBack} data-testid="iv-exit">← Back</button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && !done && (
        <div className="iv-card" data-testid="iv-step-stage">
          <div className="dyn-banner"><i className="ti ti-circle-check" style={{ fontSize: 15, color: "#3C3489" }} aria-hidden="true" /><span className="dyn-t">Investor type: <strong>{label}</strong></span></div>
          <div className="sec-t">Which rounds do you want to fund?</div>
          <div className="sec-d">Select one or more. This shapes the deal flow you'll see.</div>
          <div className="stage-row">
            {STAGES.map((s) => (
              <button key={s.name} className={`sg ${stages.includes(s.name) ? "on" : ""}`} onClick={() => toggleStage(s.name)} data-testid={`iv-stage-${s.name.split(" ")[0].toLowerCase()}`}>
                <div className="sg-h"><span className="sg-chk">{stages.includes(s.name) && <i className="ti ti-check" style={{ fontSize: 9, color: "#fff" }} aria-hidden="true" />}</span> {s.name}</div>
                <div className="sg-d">{s.desc}</div>
              </button>
            ))}
          </div>
          <div className="iv-foot" style={{ marginTop: 14 }}>
            <button className="back" onClick={() => setStep(1)} data-testid="iv-back-1">← Back</button>
            <span className="note" />
            <button className="submit" onClick={() => setStep(3)} data-testid="iv-continue">Continue →</button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && !done && (
        <div className="iv-card" data-testid="iv-step-form">
          <div className="dyn-banner"><i className="ti ti-circle-check" style={{ fontSize: 15, color: "#3C3489" }} aria-hidden="true" /><span className="dyn-t">Profile: <strong>{label}</strong> · Funding: <strong>{stages.length ? stages.join(", ") : "any round"}</strong></span></div>
          <div className="sec-t">Your contact details</div>
          <div className="sec-d">Our capital team reaches out within 2 business days to verify and onboard you.</div>

          <div className="row2">
            <div className="fg"><label className="fl">Contact name <span className="req">*</span></label><input className="fi" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" data-testid="iv-name" /></div>
            <div className="fg"><label className="fl">{orgLabel}</label><input className="fi" value={form.org} onChange={(e) => set("org", e.target.value)} placeholder="Leave blank if individual" data-testid="iv-org" /></div>
          </div>
          <div className="row2">
            <div className="fg"><label className="fl">Email <span className="req">*</span></label><input className="fi" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="iv-email" /></div>
            <div className="fg"><label className="fl">Mobile <span className="req">*</span></label><input className="fi" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/[^\d+\-\s()]/g, ""))} placeholder="+91" data-testid="iv-mobile" /></div>
          </div>
          <div className="row2">
            <div className="fg">
              <label className="fl">Approx. amount to invest (INR) <span className="req">*</span></label>
              <select className="fs" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="iv-amount">
                <option value="">Select a range…</option>
                {AMOUNTS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="fg">
              <label className="fl">Deployment horizon</label>
              <select className="fs" value={form.horizon} onChange={(e) => set("horizon", e.target.value)} data-testid="iv-horizon">
                <option value="">Select…</option>
                {HORIZONS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          </div>
          {NEEDS_REG[typeKey] && (
            <div className="fg"><label className="fl">SEBI / RBI registration number</label><input className="fi" value={form.reg} onChange={(e) => set("reg", e.target.value)} placeholder="Required for QIB / bank / NBFC / AIF" data-testid="iv-reg" /></div>
          )}

          <div className="iv-foot">
            <button className="back" onClick={() => setStep(2)} disabled={submitting} data-testid="iv-back-2">← Back</button>
            <span className="note"><i className="ti ti-shield-check" style={{ fontSize: 13, color: "#0F6E56" }} aria-hidden="true" /> Indicative amount only — no commitment. We verify before sharing any deal.</span>
            <button className="submit" onClick={handleSubmit} disabled={submitting} data-testid="iv-submit">{submitting ? "Submitting…" : "Submit & request access →"}</button>
          </div>
        </div>
      )}

      {/* DONE */}
      {done && (
        <div className="iv-card" data-testid="iv-step-done">
          <div className="iv-done">
            <div className="iv-done-ic"><i className="ti ti-circle-check" aria-hidden="true" /></div>
            <div className="iv-done-h">Request received</div>
            <div className="iv-done-sub">
              Thanks, <strong>{form.name.trim() || "investor"}</strong>. Your <strong>{label}</strong> request to join the IPO
              Funding Platform{stages.length ? <> for <strong>{stages.join(", ")}</strong></> : null} has been submitted. Our
              capital team will verify your profile and reach out within 2 business days to open curated, KYC-checked deal flow.
            </div>
            <div className="iv-foot" style={{ justifyContent: "center", marginTop: 18 }}>
              <button className="submit" onClick={onBack} data-testid="iv-done-back">Back to dashboard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JoinInvestorFlow;
